/**
 * Room types for "shop per room" (Evelien's v2, agreed 2026-10-06): the chips on the Open Home
 * shop, the room-type submenu under "New" and "More rooms like this". A room has a free name the
 * lister typed ("Woonkamer", "Living room upstairs"); its type is read from that name in any of
 * the six site languages. A room whose name matches nothing is "other".
 */

export const ROOM_TYPES = [
  "living",
  "bedroom",
  "dining",
  "study",
  "hallway",
  "outdoor",
] as const;

export type RoomType = (typeof ROOM_TYPES)[number] | "other";

/** Lower-case word stems per type, in EN, NL, DE, FR, ES and IT. Checked in this order. */
const KEYWORDS: Record<(typeof ROOM_TYPES)[number], string[]> = {
  living: [
    "living",
    "lounge",
    "sitting room",
    "family room",
    "woonkamer",
    "zitkamer",
    "huiskamer",
    "wohnzimmer",
    "salon",
    "séjour",
    "sejour",
    "salón",
    "sala de estar",
    "soggiorno",
    "salotto",
  ],
  bedroom: [
    "bedroom",
    "guest room",
    "slaapkamer",
    "logeerkamer",
    "schlafzimmer",
    "gästezimmer",
    "chambre",
    "dormitorio",
    "habitación",
    "habitacion",
    "camera da letto",
  ],
  dining: [
    "dining",
    "eetkamer",
    "eethoek",
    "esszimmer",
    "salle à manger",
    "salle a manger",
    "comedor",
    "sala da pranzo",
  ],
  study: [
    "study",
    "office",
    "werkkamer",
    "studeerkamer",
    "kantoor",
    "arbeitszimmer",
    "büro",
    "buro",
    "bureau",
    "despacho",
    "estudio",
    "ufficio",
    "studio",
  ],
  hallway: [
    // Whole words: "hall" alone does not match "Hallway", the English label of this type.
    "hallway",
    "hall",
    "entryway",
    "entrance",
    "corridor",
    "foyer",
    "hal",
    "entree",
    "gang",
    "flur",
    "diele",
    "eingang",
    "entrée",
    "couloir",
    "recibidor",
    "pasillo",
    "ingresso",
    "corridoio",
  ],
  outdoor: [
    "garden",
    "balcony",
    "terrace",
    "patio",
    "outdoor",
    "tuin",
    "balkon",
    "terras",
    "buiten",
    "garten",
    "terrasse",
    "jardin",
    "balcon",
    "jardín",
    "terraza",
    "balcón",
    "giardino",
    "terrazza",
    "balcone",
  ],
};

const matches = (name: string, keyword: string) =>
  // Whole words only: "hal" must not match "half", "gang" must not match "gangpad" in a title.
  new RegExp(`(^|[^\\p{L}])${keyword}([^\\p{L}]|$)`, "u").test(name);

export const roomTypeOf = (name: string | null | undefined): RoomType => {
  const lower = (name ?? "").toLowerCase();
  const found = ROOM_TYPES.find(type =>
    KEYWORDS[type].some(keyword => matches(lower, keyword))
  );

  return found ?? "other";
};

export const isRoomType = (value: unknown): value is RoomType =>
  value === "other" ||
  ROOM_TYPES.includes(value as (typeof ROOM_TYPES)[number]);
